import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import StudyStayLogo from '../components/StudyStayLogo';
import PublicNavbar from '../components/PublicNavbar';
import { 
  Search, 
  MessageCircle, 
  FileText, 
  CalendarCheck, 
  Shield, 
  Star, 
  ArrowRight, 
  Building2,
  CheckCircle,
  Users,
  Smartphone,
  ChevronRight,
  MapPin,
  Mail,
  Heart,
  Eye,
  Zap,
  Clock
} from 'lucide-react';

export default function Welcome() {
  const [isVisible, setIsVisible] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const statsRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);

  // Fade in on mount
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Intersection observer for stats animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !statsVisible) {
          setStatsVisible(true);
        }
      },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, [statsVisible]);

  // Auto-rotate testimonials
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTestimonial(prev => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const features = [
    {
      icon: Search,
      title: "Zoek & Ontdek",
      description: "Doorzoek honderden geverifieerde studentenkamers met slimme filters op prijs, locatie en voorzieningen.",
      color: "from-blue-500 to-blue-600"
    },
    {
      icon: MessageCircle,
      title: "Direct Contact",
      description: "Chat rechtstreeks met verhuurders via de in-app chat. Snel antwoord, geen e-mail gedoe.",
      color: "from-emerald-500 to-emerald-600"
    },
    {
      icon: CalendarCheck,
      title: "Plan Bezichtigingen",
      description: "Boek een bezichtiging met één klik. Kies een tijdslot en ontvang direct bevestiging.",
      color: "from-violet-500 to-violet-600"
    },
    {
      icon: FileText,
      title: "Digitale Contracten",
      description: "Onderteken je huurcontract digitaal. Veilig, snel en altijd beschikbaar in je account.",
      color: "from-amber-500 to-amber-600"
    }
  ];

  const steps = [
    {
      number: "01",
      title: "Maak een account",
      description: "Registreer gratis als student of verhuurder in minder dan 2 minuten.",
      icon: Users
    },
    {
      number: "02",
      title: "Zoek je droomkot",
      description: "Filter op stad, prijs, oppervlakte en voorzieningen om het perfecte kot te vinden.",
      icon: Search
    },
    {
      number: "03",
      title: "Contacteer & bezichtig",
      description: "Stuur een bericht, plan een bezichtiging en dien je motivatiebrief in.",
      icon: Eye
    },
    {
      number: "04",
      title: "Teken je contract",
      description: "Alles goedgekeurd? Teken je huurcontract digitaal en verhuis naar je nieuwe thuis.",
      icon: FileText
    }
  ];

  const testimonials = [
    {
      name: "Emma De Vries",
      role: "Studente KU Leuven",
      text: "Dankzij StudyStay heb ik mijn kot in Leuven gevonden in minder dan een week. De chat met de verhuurder was super handig!",
      rating: 5
    },
    {
      name: "Thomas Janssens",
      role: "Student UGent",
      text: "Eindelijk een platform dat begrijpt wat studenten nodig hebben. De bezichtigingen plannen was zo gemakkelijk.",
      rating: 5
    },
    {
      name: "Marc Peeters",
      role: "Verhuurder Antwerpen",
      text: "Als verhuurder bereik ik nu veel meer studenten. Het contractsysteem bespaart me enorm veel tijd.",
      rating: 5
    }
  ];

  const landlordBenefits = [
    { icon: Zap, text: "Bereik duizenden studenten" },
    { icon: FileText, text: "Digitale contracten" },
    { icon: CalendarCheck, text: "Automatische bezichtigingen" },
    { icon: MessageCircle, text: "In-app chat met huurders" },
    { icon: Clock, text: "Bespaar uren administratie" },
    { icon: Shield, text: "Veilig & betrouwbaar" }
  ];

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      <PublicNavbar />

      {/* ─── HERO SECTION ─── */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full opacity-[0.07]" style={{ background: 'radial-gradient(circle, #5EABEB 0%, transparent 70%)' }} />
          <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full opacity-[0.05]" style={{ background: 'radial-gradient(circle, #5EABEB 0%, transparent 70%)' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full opacity-[0.03]" style={{ background: 'radial-gradient(circle, #5EABEB 0%, transparent 60%)' }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 text-sm font-medium text-[#2B6FA6] mb-8">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#5EABEB] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#5EABEB]"></span>
              </span>
              Nu beschikbaar in heel België
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-slate-900 tracking-tight leading-[1.1] mb-6">
              Vind jouw ideale{' '}
              <span className="relative">
                <span className="relative z-10 bg-gradient-to-r from-[#5EABEB] to-[#2B6FA6] bg-clip-text text-transparent">
                  studentenkot
                </span>
                <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 300 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2 8C50 2 100 2 150 6C200 10 250 4 298 8" stroke="#5EABEB" strokeWidth="3" strokeLinecap="round" strokeOpacity="0.3" />
                </svg>
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto mb-10 leading-relaxed">
              Zoek, vergelijk en huur je studentenkamer op één platform. 
              Chat met verhuurders, plan bezichtigingen en teken je contract — alles digitaal.
            </p>
        
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
              <Link
                to="/user-type-login?source=welcome&userType=tenant"
                className="group inline-flex items-center justify-center px-8 py-4 text-white font-semibold text-lg rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 w-full sm:w-auto"
                style={{ background: 'linear-gradient(135deg, #5EABEB 0%, #2B6FA6 100%)' }}
              >
                <Search className="w-5 h-5 mr-2" />
                Ik zoek een kot
                <ArrowRight className="w-5 h-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/user-type-login?source=welcome&userType=landlord&landlordType=private&mode=register"
                className="group inline-flex items-center justify-center px-8 py-4 bg-white text-slate-800 font-semibold text-lg rounded-2xl shadow-sm border-2 border-slate-200 hover:border-[#5EABEB] hover:shadow-md transition-all duration-300 hover:-translate-y-1 w-full sm:w-auto"
              >
                <Building2 className="w-5 h-5 mr-2 text-[#5EABEB]" />
                Ik ben verhuurder
                <ChevronRight className="w-5 h-5 ml-1 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>

            <p className="text-sm text-slate-500">
              Gratis voor studenten · Geen verborgen kosten · Direct starten
            </p>
            <Link
              to="/user-type-login?source=welcome&userType=landlord"
              className="inline-block mt-3 text-sm text-slate-500 hover:text-[#2B6FA6] transition-colors duration-200"
            >
              Al een verhuurder-account? <span className="font-semibold underline underline-offset-2">Inloggen</span>
            </Link>
          </div>
        </div>
      </section>

      {/* ─── VALUE PROPS BAR ─── */}
      <section ref={statsRef} className="relative py-12 border-y border-slate-100" style={{ background: 'linear-gradient(180deg, #F8FBFF 0%, #FFFFFF 100%)' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`grid grid-cols-2 md:grid-cols-4 gap-8 transition-all duration-700 ${statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            {[
              { icon: CheckCircle, label: '100% Gratis', sub: 'voor studenten', color: 'text-emerald-600', bg: 'bg-emerald-50' },
              { icon: MessageCircle, label: 'Direct contact', sub: 'in-app chat', color: 'text-[#5EABEB]', bg: 'bg-blue-50' },
              { icon: FileText, label: 'Digitaal contract', sub: 'veilig ondertekenen', color: 'text-violet-600', bg: 'bg-violet-50' },
              { icon: Shield, label: 'Geverifieerd', sub: 'veilig platform', color: 'text-amber-600', bg: 'bg-amber-50' }
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="text-center flex flex-col items-center">
                  <div className={`w-11 h-11 ${item.bg} rounded-xl flex items-center justify-center mb-3`}>
                    <Icon className={`w-5 h-5 ${item.color}`} />
                  </div>
                  <div className="text-base font-bold text-slate-900">{item.label}</div>
                  <div className="text-sm text-slate-500">{item.sub}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── FEATURES SECTION ─── */}
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Alles wat je nodig hebt, op één plek
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Van zoeken tot tekenen — StudyStay begeleidt je door het volledige verhuurproces.
            </p>
      </div>
      
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="group relative bg-white rounded-2xl p-6 border border-slate-200/80 hover:border-[#5EABEB]/30 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-5 shadow-sm group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ─── */}
      <section className="py-20 sm:py-28" style={{ background: 'linear-gradient(180deg, #F8FBFF 0%, #FFFFFF 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Hoe werkt het?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              In 4 eenvoudige stappen van zoeken naar verhuizen.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="relative text-center">
                  {/* Connector line */}
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-8 left-[60%] w-[80%] h-[2px] bg-gradient-to-r from-[#5EABEB]/30 to-[#5EABEB]/10" />
                  )}
                  <div className="relative inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white border-2 border-[#5EABEB]/20 shadow-sm mb-5">
                    <Icon className="w-7 h-7 text-[#5EABEB]" />
                    <span className="absolute -top-2 -right-2 w-7 h-7 rounded-lg bg-gradient-to-br from-[#5EABEB] to-[#2B6FA6] text-white text-xs font-bold flex items-center justify-center shadow-sm">
                      {step.number.replace('0', '')}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed max-w-xs mx-auto">{step.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── FOR STUDENTS ─── */}
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-sm font-medium text-[#2B6FA6] mb-6">
                <Heart className="w-4 h-4" />
                Voor studenten
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Gratis voor elke student
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Geen verborgen kosten, geen commissies. Zoek kamers, plan bezichtigingen en chat met verhuurders — alles volledig gratis.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  'Onbeperkt zoeken en filteren',
                  'Direct chatten met verhuurders',
                  'Bezichtigingen met één klik plannen',
                  'Digitaal contract tekenen',
                  'Motivatiebrief uploaden',
                  'Favorieten bewaren'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
            </div>
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
                <Link
                  to="/user-type-login?source=welcome&userType=tenant"
                className="group inline-flex items-center px-6 py-3 font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5"
                style={{ background: 'linear-gradient(135deg, #5EABEB 0%, #2B6FA6 100%)' }}
                >
                Start met zoeken
                <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
            </div>

            {/* Decorative card mockup */}
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-3xl border border-slate-200/80 p-8 shadow-xl">
                {/* Fake property card */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-4">
                  <div className="h-40 bg-gradient-to-br from-[#5EABEB]/20 to-[#2B6FA6]/10 flex items-center justify-center">
                    <Building2 className="w-16 h-16 text-[#5EABEB]/40" />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-slate-900">Studentenkamer Leuven</h4>
                      <Heart className="w-5 h-5 text-red-400 fill-red-400" />
                    </div>
                    <p className="text-sm text-slate-500 mb-3 flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" /> Naamsestraat, 3000 Leuven
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xl font-bold text-[#2B6FA6]">€450<span className="text-sm font-normal text-slate-500">/maand</span></span>
                      <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-lg">Beschikbaar</span>
                    </div>
                  </div>
                </div>
                {/* Chat preview */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-full bg-[#5EABEB]/10 flex items-center justify-center">
                      <MessageCircle className="w-4 h-4 text-[#5EABEB]" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">Verhuurder</p>
                      <p className="text-xs text-slate-500">Online</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="bg-slate-100 rounded-xl rounded-tl-sm px-3 py-2 text-sm text-slate-700 max-w-[80%]">
                      Hallo! Interesse in de kamer? 👋
                    </div>
                    <div className="bg-[#5EABEB] rounded-xl rounded-tr-sm px-3 py-2 text-sm text-white max-w-[80%] ml-auto">
                      Ja, kan ik een bezichtiging plannen?
                    </div>
                  </div>
                </div>
              </div>
              {/* Floating badge */}
              <div className="absolute -top-4 -right-4 bg-white rounded-xl shadow-lg border border-slate-200 px-4 py-2.5 flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Contract</p>
                  <p className="text-sm font-semibold text-emerald-700">Getekend ✓</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FOR LANDLORDS ─── */}
      <section className="py-20 sm:py-28" style={{ background: 'linear-gradient(180deg, #F8FBFF 0%, #F0F7FF 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Benefits grid */}
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-4">
                {landlordBenefits.map((benefit, i) => {
                  const Icon = benefit.icon;
                  return (
                    <div key={i} className="bg-white rounded-xl p-5 border border-slate-200/80 shadow-sm hover:shadow-md hover:border-[#5EABEB]/30 transition-all duration-300">
                      <Icon className="w-6 h-6 text-[#5EABEB] mb-3" />
                      <p className="text-sm font-medium text-slate-800">{benefit.text}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-100 text-sm font-medium text-amber-700 mb-6">
                <Building2 className="w-4 h-4" />
                Voor verhuurders
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Verhuur slimmer, niet harder
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Bereik duizenden studenten, beheer bezichtigingen, genereer contracten en communiceer — alles vanuit één overzichtelijk dashboard.
              </p>
              <div className="flex flex-col items-start gap-2">
                <Link
                  to="/user-type-login?source=welcome&userType=landlord&landlordType=private&mode=register"
                  className="group inline-flex items-center px-6 py-3 font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5"
                  style={{ background: 'linear-gradient(135deg, #5EABEB 0%, #2B6FA6 100%)' }}
                >
                  Start als verhuurder
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/user-type-login?source=welcome&userType=landlord"
                  className="text-sm text-slate-500 hover:text-[#2B6FA6] transition-colors duration-200"
                >
                  Al een account? <span className="font-semibold underline underline-offset-2">Inloggen</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── TESTIMONIALS ─── */}
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Wat gebruikers zeggen
            </h2>
            <p className="text-lg text-slate-600">
              Duizenden studenten en verhuurders vertrouwen op StudyStay.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="relative bg-white rounded-2xl border border-slate-200/80 shadow-lg p-8 sm:p-10">
              {/* Quote icon */}
              <div className="absolute -top-5 left-8 w-10 h-10 rounded-xl bg-gradient-to-br from-[#5EABEB] to-[#2B6FA6] flex items-center justify-center shadow-md">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10H14.017zM0 21v-7.391c0-5.704 3.731-9.57 8.983-10.609L9.978 5.151c-2.432.917-3.995 3.638-3.995 5.849h4v10H0z" />
                </svg>
              </div>

              <div className="min-h-[140px]">
                {testimonials.map((testimonial, index) => (
                  <div
                    key={index}
                    className={`transition-all duration-500 ${
                      index === activeTestimonial ? 'opacity-100' : 'opacity-0 absolute inset-0 pointer-events-none'
                    }`}
                  >
                    <p className="text-lg sm:text-xl text-slate-700 leading-relaxed mb-6 italic">
                      "{testimonial.text}"
                    </p>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#5EABEB]/20 to-[#2B6FA6]/10 flex items-center justify-center">
                        <span className="text-lg font-bold text-[#2B6FA6]">
                          {testimonial.name.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{testimonial.name}</p>
                        <p className="text-sm text-slate-500">{testimonial.role}</p>
                      </div>
                      <div className="ml-auto flex gap-0.5">
                        {Array.from({ length: testimonial.rating }).map((_, i) => (
                          <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Dots */}
              <div className="flex justify-center gap-2 mt-8">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveTestimonial(index)}
                    className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                      index === activeTestimonial 
                        ? 'bg-[#5EABEB] w-8' 
                        : 'bg-slate-300 hover:bg-slate-400'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── APP PROMOTION ─── */}
      <section className="py-20 sm:py-28" style={{ background: 'linear-gradient(180deg, #F0F7FF 0%, #FFFFFF 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-8 sm:p-12 lg:p-16 overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#5EABEB]/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#5EABEB]/10 rounded-full blur-3xl" />
            
            <div className="relative grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/10 text-sm font-medium text-blue-300 mb-6">
                  <Smartphone className="w-4 h-4" />
                  Ook beschikbaar als app
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                  Neem StudyStay overal mee
                </h2>
                <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                  Download de gratis iOS-app en ontvang push-notificaties bij nieuwe berichten, bezichtigingen en contractupdates.
                </p>
                <a
                  href="https://apps.apple.com/app/studystay"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-white text-slate-900 pl-4 pr-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
                >
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <div className="text-left">
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider leading-none">Download in de</div>
                    <div className="text-base font-bold leading-tight">App Store</div>
                  </div>
                </a>
              </div>

              {/* Phone mockup */}
              <div className="hidden lg:flex justify-center">
                <div className="relative w-64">
                  <div className="bg-slate-700 rounded-[2.5rem] p-3 shadow-2xl border border-slate-600">
                    <div className="bg-white rounded-[2rem] overflow-hidden">
                      {/* Status bar */}
                      <div className="bg-[#5EABEB] px-6 py-4">
                        <div className="flex items-center gap-2">
                          <StudyStayLogo className="w-6 h-6" />
                          <span className="text-white font-semibold text-sm">StudyStay</span>
                        </div>
                      </div>
                      {/* App content mockup */}
                      <div className="p-4 space-y-3">
                        <div className="h-24 bg-slate-100 rounded-xl" />
                        <div className="h-4 bg-slate-100 rounded w-3/4" />
                        <div className="h-3 bg-slate-100 rounded w-1/2" />
                        <div className="h-20 bg-slate-100 rounded-xl" />
                        <div className="h-4 bg-slate-100 rounded w-2/3" />
                        <div className="h-3 bg-slate-100 rounded w-1/3" />
                      </div>
                </div>
                  </div>
                  {/* Notification badge */}
                  <div className="absolute -top-3 -right-3 bg-white rounded-xl shadow-lg border border-slate-200 px-3 py-2 flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                    <span className="text-xs font-medium text-slate-700">Nieuw bericht</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ─── */}
      <section className="py-20 sm:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
            Klaar om je kot te vinden?
          </h2>
          <p className="text-lg text-slate-600 mb-10 max-w-2xl mx-auto">
            Sluit je aan bij duizenden studenten en verhuurders die al gebruik maken van StudyStay.
          </p>
          <Link
            to="/user-type-login?source=welcome&userType=tenant"
            className="group inline-flex items-center justify-center px-10 py-4 text-white font-semibold text-lg rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            style={{ background: 'linear-gradient(135deg, #5EABEB 0%, #2B6FA6 100%)' }}
          >
            Registreer gratis
            <ArrowRight className="w-5 h-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="border-t border-slate-200/80 bg-gradient-to-b from-white to-[#F8FBFF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-12 md:py-16">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
              {/* Company */}
              <div className="space-y-4">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center overflow-hidden shadow-sm border border-slate-200/60" style={{ backgroundColor: '#60ADF0' }}>
                    <StudyStayLogo className="w-6 h-6" />
                  </div>
                  <span className="text-lg font-bold text-slate-900">StudyStay</span>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Het platform dat studenten en verhuurders samenbrengt. Zoek, chat, bezichtig en teken — alles op één plek.
                </p>
                <div className="text-xs text-slate-500 space-y-0.5">
                  <p><strong>Nolf Technologies OÜ</strong></p>
                  <p>Tornimäe tn 5, 10145 Tallinn, Estonia</p>
                </div>
              </div>

              {/* Voor studenten */}
              <div className="space-y-4">
                <h4 className="font-semibold text-slate-900">Voor Studenten</h4>
                <ul className="space-y-2.5">
                  {[
                    { label: 'Zoek kamers', to: '/user-type-login?source=welcome&userType=tenant' },
                    { label: 'Hoe werkt het?', to: '#' },
                    { label: 'Veelgestelde vragen', to: '#' }
                  ].map((link, i) => (
                    <li key={i}>
                      <Link to={link.to} className="text-sm text-slate-600 hover:text-[#2B6FA6] transition-colors duration-200">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
                </div>

              {/* Voor verhuurders */}
              <div className="space-y-4">
                <h4 className="font-semibold text-slate-900">Voor Verhuurders</h4>
                <ul className="space-y-2.5">
                  {[
                    { label: 'Start als verhuurder', to: '/user-type-login?source=welcome&userType=landlord&landlordType=private&mode=register' },
                    { label: 'Abonnementen', to: '/voor-verhuurders#pricing' },
                    { label: 'Hulp nodig?', to: '/voor-verhuurders#afspraak' }
                  ].map((link, i) => (
                    <li key={i}>
                      <Link to={link.to} className="text-sm text-slate-600 hover:text-[#2B6FA6] transition-colors duration-200">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Contact & Legal */}
              <div className="space-y-4">
                <h4 className="font-semibold text-slate-900">Contact & Juridisch</h4>
                <ul className="space-y-2.5">
                  <li>
                    <Link to="/contact" className="text-sm text-slate-600 hover:text-[#2B6FA6] transition-colors duration-200 flex items-center gap-2">
                      <Mail className="w-3.5 h-3.5" /> Contact
                    </Link>
                  </li>
                  <li>
                    <Link to="/privacy" className="text-sm text-slate-600 hover:text-[#2B6FA6] transition-colors duration-200">
                      Privacybeleid
                    </Link>
                  </li>
                  <li>
                    <Link to="/terms" className="text-sm text-slate-600 hover:text-[#2B6FA6] transition-colors duration-200">
                      Algemene Voorwaarden
                    </Link>
                  </li>
                  <li>
                    <Link to="/cookies" className="text-sm text-slate-600 hover:text-[#2B6FA6] transition-colors duration-200">
                      Cookiebeleid
                    </Link>
                  </li>
                  <li>
                    <Link to="/legal" className="text-sm text-slate-600 hover:text-[#2B6FA6] transition-colors duration-200">
                      Alle juridische documenten
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-slate-200/80 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-500">
              © {new Date().getFullYear()} Nolf Technologies OÜ. Alle rechten voorbehouden.
            </p>
            <p className="text-xs text-slate-400">
              Made with ❤️ for students
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
